/**
 * 四段子流程（现场确认 / 客退入库 / 补货 / 退货）：判断是否已提交、是否允许再编辑。
 * 与详情只读区块、列表/详情办理入口、`RMAOrderCreateForm` 块展示共用。
 *
 * 列表 `QueryDto` 行对象若缺少附件/数量等字段，`rmaSegmentHasSubmittedData` 可能无法识别「已提交」，
 * 此时 `rmaSegmentEditable` 仍可能为 true（仅靠 isShow）；以详情 `GetOrderDetail` 为准可避免误隐藏。
 */

import { RMA_ORDER_STATUS } from './rmaOrderConstants'
import {
  rmaIsShowCustomerReturnWarehouseInbound,
  rmaIsShowOnSiteConfirmation,
  rmaIsShowReplenishmentProcess,
  rmaIsShowReturnProcess,
} from './rmaOrderFlags'

/** 与 {@link RMAOrder.vue} statusNum / 表单 pickFirstNum 一致 */
export function rmaPickFirstNum(obj, keys) {
  if (!obj) return null
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue
    const v = obj[k]
    if (v === null || v === undefined || v === '') continue
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return null
}

/** 用于日期等原始字段 */
export function rmaPickFirstRaw(obj, keys) {
  if (!obj) return null
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue
    const v = obj[k]
    if (v != null && v !== '') return v
  }
  return null
}

export function rmaPickFirstNonEmptyStr(obj, keys) {
  if (!obj) return ''
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i]
    if (!Object.prototype.hasOwnProperty.call(obj, k)) continue
    const v = obj[k]
    if (v == null || v === '') continue
    const s = typeof v === 'string' ? v.trim() : String(v).trim()
    if (s) return s
  }
  return ''
}

export function rmaOrderStatusNum(s) {
  if (s == null) return -1
  return Number(s)
}

export function getOnsiteConfirmMaterialFilesArray(order) {
  if (!order) return []
  const raw =
    order.onsiteConfirmMaterialFiles != null
      ? order.onsiteConfirmMaterialFiles
      : order.OnsiteConfirmMaterialFiles
  return Array.isArray(raw) ? raw : []
}

export function getReturnWarehouseInFilesArray(order) {
  if (!order) return []
  const raw =
    order.returnWarehouseInFiles != null
      ? order.returnWarehouseInFiles
      : order.ReturnWarehouseInFiles
  return Array.isArray(raw) ? raw : []
}

/**
 * 该子流程段是否已有提交数据（与详情「只读展示」探测一致）。
 * @param {'onSite'|'warehouse'|'replenishment'|'return'} segment
 */
export function rmaSegmentHasSubmittedData(order, segment) {
  if (!order) return false
  const d = order
  if (segment === 'onSite') {
    const q = rmaPickFirstNum(d, [
      'onsiteConfirmQuantity',
      'OnsiteConfirmQuantity',
      'onSiteConfirmationQuantity',
      'OnSiteConfirmQuantity',
    ])
    if (q !== null && !Number.isNaN(q)) return true
    if (getOnsiteConfirmMaterialFilesArray(d).length > 0) return true
    return !!(
      rmaPickFirstNonEmptyStr(d, ['onsiteOperatorName', 'OnsiteOperatorName']) ||
      rmaPickFirstNonEmptyStr(d, [
        'onsiteOperateTimeText',
        'OnsiteOperateTimeText',
        'onsiteOperateTime',
        'OnsiteOperateTime',
      ])
    )
  }
  if (segment === 'warehouse') {
    const q = rmaPickFirstNum(d, [
      'returnWarehouseInQuantity',
      'ReturnWarehouseInQuantity',
      'warehouseInboundQuantity',
      'customerReturnWarehouseInboundQuantity',
      'WarehouseInboundQuantity',
    ])
    if (q !== null && !Number.isNaN(q)) return true
    if (getReturnWarehouseInFilesArray(d).length > 0) return true
    return !!(
      rmaPickFirstNonEmptyStr(d, ['warehouseInOperatorName', 'WarehouseInOperatorName']) ||
      rmaPickFirstNonEmptyStr(d, ['warehouseInOperateTime', 'WarehouseInOperateTime'])
    )
  }
  if (segment === 'replenishment') {
    const q = rmaPickFirstNum(d, [
      'replenishmentQuantity',
      'replenishmentProcessQuantity',
      'ReplenishmentQuantity',
    ])
    if (q !== null && !Number.isNaN(q)) return true
    for (const k of [
      'replenishmentCompleteDate',
      'replenishmentProcessCompleteDate',
      'ReplenishmentCompleteDate',
      'replenishmentCompletedTime',
      'ReplenishmentCompletedTime',
    ]) {
      if (Object.prototype.hasOwnProperty.call(d, k) && d[k] != null && d[k] !== '') return true
    }
    return !!(
      rmaPickFirstNonEmptyStr(d, ['replenishmentOperatorName', 'ReplenishmentOperatorName']) ||
      rmaPickFirstNonEmptyStr(d, ['replenishmentOperateTimeText', 'ReplenishmentOperateTimeText'])
    )
  }
  if (segment === 'return') {
    const q = rmaPickFirstNum(d, [
      'returnQuantity',
      'ReturnQuantity',
      'returnProcessQuantity',
      'returnHandlingQuantity',
      'ReturnProcessQuantity',
    ])
    if (q !== null && !Number.isNaN(q)) return true
    for (const k of [
      'returnProcessCompleteDate',
      'returnHandlingCompleteDate',
      'ReturnProcessCompleteDate',
      'returnCompletedTime',
      'ReturnCompletedTime',
    ]) {
      if (Object.prototype.hasOwnProperty.call(d, k) && d[k] != null && d[k] !== '') return true
    }
    return !!(
      rmaPickFirstNonEmptyStr(d, ['returnOperatorName', 'ReturnOperatorName']) ||
      rmaPickFirstNonEmptyStr(d, ['returnOperateTime', 'ReturnOperateTime'])
    )
  }
  return false
}

/**
 * 是否显示列表/详情办理入口与子流程表单块：`isShow` 且未达到「≥处理中且该段已提交」。
 */
export function rmaSegmentEditable(order, segment) {
  if (!order) return false
  const isShow =
    segment === 'onSite'
      ? rmaIsShowOnSiteConfirmation(order)
      : segment === 'warehouse'
        ? rmaIsShowCustomerReturnWarehouseInbound(order)
        : segment === 'replenishment'
          ? rmaIsShowReplenishmentProcess(order)
          : segment === 'return'
            ? rmaIsShowReturnProcess(order)
            : false
  if (!isShow) return false
  const st = rmaOrderStatusNum(order.status)
  if (st < RMA_ORDER_STATUS.IN_PROGRESS) return true
  return !rmaSegmentHasSubmittedData(order, segment)
}

/** 与 RMAOrderCreateForm mapPictureFilesToPics 一致：子流程附件回填可编辑列表 */
export function mapPictureFilesToWorkflowPics(arr) {
  if (!Array.isArray(arr)) return []
  return arr
    .map((p) => {
      const g = p.fileGuids || p.guid
      const u = p.url != null && p.url !== '' ? p.url : p.imageUrl || ''
      const fn = p.fileName != null ? p.fileName : p.FileName
      return g ? { fileGuids: g, url: u || '', fileName: fn || '' } : null
    })
    .filter(Boolean)
}
